import { useCallback, useEffect, useState } from "react";
import { Badge } from "antd";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, Spinner, Text } from "components/common";
import NotificationItem from "components/custom/notificationItem";
import FollowRequests from "features/community/components/FollowRequests";
import {
  useGetUnreadCountQuery,
  useLazyGetNotificationsQuery,
  useMarkNotificationsReadMutation,
} from "features/community/apis";
import theme from "utils/themeVars";
import { NOTIFICATIONS_PATH } from "constants/app-paths";
import styles from "./index.module.less";

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const { data: unread } = useGetUnreadCountQuery(undefined, { refetchOnMountOrArgChange: true });
  const unreadCount = unread?.unreadCount || 0;
  const [triggerNotifs, { isFetching, isError }] = useLazyGetNotificationsQuery();
  const [markRead] = useMarkNotificationsReadMutation();

  const refresh = useCallback(async () => {
    const result = await triggerNotifs({ limit: 5 });
    if (result?.data) setItems(result.data.data || []);
  }, [triggerNotifs]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);
  useEffect(() => {
    const update = () => { if (open) refresh(); };
    window.addEventListener("social:notification", update);
    window.addEventListener("social:follow_request", update);
    return () => {
      window.removeEventListener("social:notification", update);
      window.removeEventListener("social:follow_request", update);
    };
  }, [open, refresh]);

  const openNotification = async (item, destination) => {
    if (!item.is_read) {
      setItems((current) => current.map((entry) => entry.notification_id === item.notification_id ? { ...entry, is_read: true } : entry));
      await markRead({ ids: [item.notification_id] });
    }
    setOpen(false);
    if (destination) navigate(destination);
  };

  const markAll = async () => {
    await markRead({ all: true });
    setItems((current) => current.map((item) => ({ ...item, is_read: true })));
  };

  return (
    <>
      <Badge count={unreadCount} overflowCount={99} size="small" offset={[-4, 4]}>
        <Button icon="UserNotification" iconProps={{ size: "16px" }} shape="circle" style={{ border: "1px solid #c5c6d0" }} onClick={() => setOpen(true)} aria-label={`${unreadCount} unread notifications`} />
      </Badge>
      <Drawer title="Notifications" visible={open} onClose={() => setOpen(false)} width={420} extra={unreadCount > 0 ? <Button size="small" type="light" color={theme["primary-color"]} text="Mark all read" onClick={markAll} /> : null}>
        <div className={styles.requests}><FollowRequests compact /></div>
        {isFetching && items.length === 0 ? <div className={styles.loading}><Spinner /></div> : isError && items.length === 0 ? <div className={styles.state}><Text>Unable to load notifications.</Text><Button size="small" type="default" text="Retry" onClick={refresh} /></div> : items.length === 0 ? <div className={styles.state}><Text>You’re all caught up.</Text><Text>New followers and recommendations will appear here.</Text></div> : <div className={styles.list}>{items.map((item) => <NotificationItem key={item.notification_id} item={item} compact onOpen={openNotification} />)}</div>}
        <Button type="light" text="View all notifications" className={styles.viewAll} onClick={() => { setOpen(false); navigate(NOTIFICATIONS_PATH); }} />
      </Drawer>
    </>
  );
};

export default NotificationBell;
